import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth, ALL_FEATURES_ENABLED, type FeatureKey, type SiteFeatures } from './AuthContext';
import { api } from '@/lib/api';

const STORAGE_KEY = 'ctc-selected-site-slug';

export interface AccessibleSite {
  slug: string;
  name: string;
  active: boolean;
  features: SiteFeatures;
}

interface AdminSiteRow {
  id: string;
  slug: string;
  name: string;
  active: boolean;
  featureScan: boolean;
  featureTours: boolean;
  featureRounding: boolean;
  featureDar: boolean;
  featureStorage: boolean;
  featureCensus: boolean;
  featureAlerts: boolean;
}

/**
 * Resolves the user's accessible sites. For Global.Admin we fetch the active
 * sites list (they don't have explicit user_site_access rows); for everyone
 * else we read from `me.siteAccess`. Each entry carries the per-site feature
 * map so the picker + nav can filter.
 */
export function useAccessibleSites(): AccessibleSite[] {
  const { me, isGlobalAdmin } = useAuth();
  const adminSitesQuery = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => api.get<AdminSiteRow[]>('/api/admin/sites'),
    enabled: isGlobalAdmin,
    staleTime: 30_000,
  });

  if (isGlobalAdmin) {
    return (adminSitesQuery.data ?? [])
      .filter((s) => s.active)
      .map((s) => ({
        slug: s.slug,
        name: s.name,
        active: s.active,
        features: {
          scan: s.featureScan,
          tours: s.featureTours,
          rounding: s.featureRounding,
          dar: s.featureDar,
          storage: s.featureStorage,
          census: s.featureCensus,
          alerts: s.featureAlerts,
        },
      }));
  }

  return (me?.siteAccess ?? []).map((a) => ({
    slug: a.siteSlug,
    name: a.siteName,
    active: true,
    features: a.features ?? ALL_FEATURES_ENABLED,
  }));
}

const DEFAULT_FEATURE_ORDER: FeatureKey[] = [
  'scan',
  'rounding',
  'dar',
  'storage',
  'tours',
  'census',
  'alerts',
];

function firstEnabledFeaturePath(slug: string, features: SiteFeatures): string {
  for (const f of DEFAULT_FEATURE_ORDER) {
    if (features[f]) {
      if (f === 'scan') return `/sites/${slug}/scan`;
      if (f === 'rounding') return `/sites/${slug}/rounding`;
      if (f === 'dar') return `/sites/${slug}/dar`;
      if (f === 'storage') return `/sites/${slug}/storage`;
      if (f === 'tours') return `/sites/${slug}/tours`;
      if (f === 'census') return `/sites/${slug}/census`;
      if (f === 'alerts') return `/sites/${slug}/alerts`;
    }
  }
  return '/';
}

/**
 * Tracks the user's currently-selected site. Stored in localStorage so it
 * survives reloads — a guard picks DMRC at the start of their shift and the
 * sidebar/More sheet stay on DMRC even if they navigate to /.
 *
 * The URL is also a source of truth: navigating to /sites/<slug>/... mirrors
 * the slug back into localStorage so deep links + browser back work as
 * expected.
 */
export function useSelectedSite(): {
  accessibleSites: AccessibleSite[];
  selectedSlug: string | null;
  selectedSite: AccessibleSite | null;
  selectSlug: (slug: string) => void;
} {
  const accessibleSites = useAccessibleSites();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ slug: string }>('/sites/:slug/:rest*');

  const [stored, setStored] = React.useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Mirror URL slug into localStorage when navigating site-scoped routes.
  React.useEffect(() => {
    if (match && params?.slug && params.slug !== stored) {
      setStored(params.slug);
      try {
        localStorage.setItem(STORAGE_KEY, params.slug);
      } catch {
        /* ignore */
      }
    }
  }, [match, params?.slug, stored]);

  const selectedSlug = (() => {
    if (match && params?.slug) return params.slug;
    if (stored && accessibleSites.some((s) => s.slug === stored)) return stored;
    return accessibleSites[0]?.slug ?? null;
  })();

  const selectedSite = accessibleSites.find((s) => s.slug === selectedSlug) ?? null;

  const selectSlug = React.useCallback(
    (slug: string) => {
      const site = accessibleSites.find((s) => s.slug === slug);
      if (!site) return;
      setStored(slug);
      try {
        localStorage.setItem(STORAGE_KEY, slug);
      } catch {
        /* ignore */
      }
      navigate(firstEnabledFeaturePath(slug, site.features));
    },
    [accessibleSites, navigate],
  );

  return { accessibleSites, selectedSlug, selectedSite, selectSlug };
}
